import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Heart, CreditCard } from 'lucide-react';
import { API } from '../App';

function Giving() {
  const [amount, setAmount] = useState('');
  const [donationType, setDonationType] = useState('tithe');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    if (attempt >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Please check your email for confirmation.' });
      return;
    }

    try {
      const response = await axios.get(`${API}/donations/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus({ status: 'success', message: 'Thank you for your generous donation!' });
        toast.success('Payment successful!');
      } else if (response.data.status === 'expired') {
        setPaymentStatus({ status: 'error', message: 'Payment session expired. Please try again.' });
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch (error) {
      setPaymentStatus({ status: 'error', message: 'Error verifying payment. Please contact us.' });
    }
  };

  const handleStripeCheckout = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/donations/checkout`, {
        amount: parseFloat(amount),
        type: donationType,
        donor_name: donorName || 'Anonymous',
        donor_email: donorEmail || null,
        is_recurring: isRecurring
      });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to process donation');
      setProcessing(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <div className="section-padding bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="giving-title">
            Give to Our Church
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            "Whoever is generous to the poor lends to the Lord, and he will repay him for his deed." - Proverbs 19:17
          </p>
        </div>
      </div>

      {/* Payment Status */}
      {paymentStatus && (
        <div className={`max-w-3xl mx-auto px-4 mt-8`}>
          <div className={`card p-6 text-center ${
            paymentStatus.status === 'success' ? 'bg-green-50 border-green-200' :
            paymentStatus.status === 'error' ? 'bg-red-50 border-red-200' :
            'bg-yellow-50 border-yellow-200'
          }`} data-testid="payment-status">
            <p className="text-2xl font-bold">{paymentStatus.message}</p>
          </div>
        </div>
      )}

      {/* Giving Options */}
      <div className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
            Ways to Give
          </h2>

          {/* Stripe Donation Form */}
          <div className="card p-8 mb-12">
            <div className="flex items-center mb-6">
              <CreditCard size={40} color="#1e3a8a" className="mr-4" />
              <h3 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>Online Donation</h3>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Donation Type</label>
                <select
                  value={donationType}
                  onChange={(e) => setDonationType(e.target.value)}
                  className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                  data-testid="donation-type-select"
                >
                  <option value="tithe">Tithe</option>
                  <option value="offering">Offering</option>
                  <option value="missions">Missions</option>
                  <option value="building">Building Fund</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Amount (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                  placeholder="Enter amount"
                  data-testid="donation-amount-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Your Name (Optional)</label>
                  <input
                    type="text"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="Anonymous"
                    data-testid="donor-name-input"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Email (Optional)</label>
                  <input
                    type="email"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="your@email.com"
                    data-testid="donor-email-input"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="w-6 h-6 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                  data-testid="recurring-checkbox"
                />
                <label htmlFor="recurring" className="ml-3 text-lg text-slate-700">
                  Make this a recurring monthly donation (Auto-Tithing)
                </label>
              </div>

              <button
                onClick={handleStripeCheckout}
                disabled={processing}
                className="btn-primary w-full text-xl"
                data-testid="donate-stripe-btn"
              >
                {processing ? 'Processing...' : 'Donate with Card'}
              </button>
              <p className="text-center text-sm text-slate-600">Secure payment powered by Stripe</p>
            </div>
          </div>

          {/* Other Payment Methods */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>PayPal</h3>
              <p className="text-lg text-slate-700 mb-6">Send your donation via PayPal</p>
              <a 
                href="https://www.paypal.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary block text-center"
                data-testid="paypal-link"
              >
                Donate with PayPal
              </a>
            </div>
            <div className="card p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Venmo</h3>
              <p className="text-lg text-slate-700 mb-6">Send your donation via Venmo</p>
              <a 
                href="https://venmo.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary block text-center"
                data-testid="venmo-link"
              >
                Donate with Venmo
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Why Give */}
      <div className="section-padding" style={{ backgroundColor: '#fffbeb' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart size={64} color="#1e3a8a" className="mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
            Why Give?
          </h2>
          <p className="text-xl text-slate-700 leading-relaxed mb-6">
            Your generous giving helps us fulfill our mission to love God and love others. Through your support, 
            we can continue to provide worship services, community outreach, mission work, and care for those in need.
          </p>
          <p className="text-xl text-slate-700 leading-relaxed">
            Every gift, large or small, makes a difference in the lives of those we serve. Thank you for your faithful stewardship!
          </p>
        </div>
      </div>
    </div>
  );
}

export default Giving;